import Core                  from '@/core/Core';

import type Qty              from '@/core/types/Qty';
import type Price            from '@/core/types/Price';
import type Unit             from '@/domains/Units/Unit';
import type Company          from '@/domains/Companies/Company';
import type Partner          from '@/domains/Partners/Partner';
import type Manufacturer     from '@/domains/Manufacturers/Manufacturer';
import type DateSimple       from '@/core/types/DateSimple';
import type DateRange        from '@/core/types/DateRange';
import SupplyOffer           from '@/domains/SupplyOffers/SupplyOffer';

/**
 * Расхождение предложения с требованием.
 *
 * label       — наименование поля,
 * valueSource — значение в предложении,
 * valueTarget — требуемое значение.
 */
export type MismatchItem = {
  label:       string;
  valueSource: string;
  valueTarget: string;
};

/**
 * Правила сравнения предложения с требованием.
 *
 * source — то, что предлагают: значение офера, ставки, позиции заказа.
 * target — то, что требуют: значение потребности, тендера или товара.
 *
 * Каждое правило возвращает описание расхождения либо null, если расхождения
 * нет. Пустых значений здесь не бывает: решение «есть ли что сравнивать»
 * принимает вызывающий, до входа в правило. Отбор подходящих записей — тоже
 * его дело: сюда приходит то, с чем уже надо сравнивать.
 *
 * Утилита ничего не знает про сущности — только про значения. Раскладка
 * «сущность → значения» живёт в прокси-методах моделей.
 */
class Mismatch {

  /** Предложенного количества меньше, чем требуется. */
  static getQtyMismatch(source: Qty, target: Qty, unit: Unit): string | null {
    if (source.lessThan(target)) {
      return `Требуется от ${target.format(unit)}`;
    }
    else {
      return null;
    }
  }

  /** Предложенный период получения не покрывает требуемую дату. */
  static getReceiptDateMismatch(source: DateRange, target: DateSimple): string | null {
    if (source.contains(target)) {
      return null;
    }
    else {
      return `Требуется привезти к ${target.format()}`;
    }
  }

  /** Предложенный период отгрузки не покрывает требуемый период предзаказа. */
  static getPreorderShipmentPeriodMismatch(source: DateRange, target: DateRange): string | null {
    if (source.contains(target) || source.equals(target)) {
      return null;
    }
    else {
      return `Требуется интервал получения ${target.toString()}`;
    }
  }

  /**
   * Срок годности предложения истекает раньше требуемого.
   *
   * source — дата, до которой годна партия поставщика.
   * target — минимальная дата, которая устраивает покупателя. Считает её
   * товар из своего остаточного срока годности: здесь обе стороны — даты.
   */
  static getExpirationDateMismatch(source: DateSimple, target: DateSimple): string | null {
    if (source.isBefore(target)) {
      return `Требуется срок годности как минимум до ${target.format()}`;
    }
    else {
      return null;
    }
  }

  /** Производитель предложения не тот, что требуется. */
  static getManufacturerMismatch(source: Manufacturer, target: Manufacturer): string | null {
    if (source.id === target.id) {
      return null;
    }
    else {
      return `Требуется производитель ${target.name}`;
    }
  }

  /** Производитель предложения не входит в число допустимых. */
  static getManufacturerMismatchByList(source: Manufacturer, target: Manufacturer[]): string | null {
    const allowedIds = new Set(target.map(_manufacturer => _manufacturer.id));

    if (allowedIds.has(source.id)) {
      return null;
    }
    else if (target.length === 1) {
      return `Требуется производитель ${target[0].name}`;
    }
    else {
      return `Требуется один из производителей: ${target.map(_manufacturer => _manufacturer.name).join(', ')}`;
    }
  }

  /** Юр. лицо предложения не входит в число допустимых. */
  static getSellerCompanyMismatchByList(source: Company, target: Company[]): string | null {
    const allowedIds = new Set(target.map(_company => _company.id));

    if (allowedIds.has(source.id)) {
      return null;
    }
    else if (target.length === 1) {
      return `Требуется юр. лицо ${target[0].name}`;
    }
    else {
      return `Требуется одно из юр. лиц: ${target.map(_company => _company.name).join(', ')}`;
    }
  }


  /**
   * Связка предложения не входит в число разрешённых.
   *
   * Поставщик, юр. лицо и производитель в разрешении неразрывны: взять
   * производителя из одного разрешения, а юр. лицо из другого нельзя.
   * Поэтому в множество кладётся ключ целой тройки, а не три отдельных
   * набора id.
   *
   * Приходят действующие разрешения этого поставщика — отбор делает домен.
   */
  static getBundleMismatch(
    source: { seller: Partner; sellerCompany: Company; manufacturer: Manufacturer },
    target: SupplyOffer[],
  ): string | null {
    const sourceKey   = SupplyOffer.getBundleKey(source.seller, source.sellerCompany, source.manufacturer);
    const allowedKeys = new Set(target.map(_supplyOffer => _supplyOffer.bundleKey));

    if (allowedKeys.has(sourceKey)) {
      return null;
    } else if (Core.utils.isEmptyArray(target) === true) {
      return 'Поставщика нет в разрешениях на поставку'
    }
    else {
      return `Требуется разрешённая связка поставщик — юр. лицо — производитель:\n`
        + target.map(_supplyOffer => _supplyOffer.toBundleString()).join('\n\n');
    }
  }

  /** Фасовка производителя не совпадает с требованием производства. Превышение отличается текстом — из большей упаковки производство может не работать. */
  static getPackageVolumeMismatch(source: Qty, target: Qty, unit: Unit | null): string | null {
    if (source.equals(target) === true) {
      return null;
    }
    else if (source.greaterThan(target) === true) {
      return `Фасовка больше требования производства (${target.format(unit)}) — производство может не работать с такой упаковкой`;
    }
    else {
      return `Требование производства — фасовка ${target.format(unit)}`;
    }
  }

  /** Единица измерения предложения не совпадает с требуемой. */
  static getUnitMismatch(source: Unit, target: Unit): string | null {
    if (source.id === target.id) {
      return null;
    }
    else {
      return `Требуется единица измерения ${target.name}`;
    }
  }

  /** Страховой запас по предзаказу меньше требуемого. */
  static getPreorderReserveMismatch(source: Qty, target: Qty, unit: Unit): string | null {
    if (source.lessThan(target)) {
      return `Требуется страховой запас от ${target.format(unit)}`;
    }
    else {
      return null;
    }
  }

  /** Минимальная партия по предзаказу больше, чем требуется купить. */
  static getPreorderMinQtyMismatch(source: Qty, target: Qty, unit: Unit): string | null {
    if (source.greaterThan(target)) {
      return `Минимальная партия ${source.format(unit)} больше требуемых ${target.format(unit)}`;
    }
    else {
      return null;
    }
  }

  static getPriceMismatch(
    sourcePrice: Price,
    sourceCurrencyCode: string,
    targetPrice: Price,
    targetCurrencyCode: string,
    date: Date | DateSimple,
  ): string | null {
    if (Core.utils.money.getRateFallbackMark(sourceCurrencyCode, targetCurrencyCode, date) !== null) {
      /** Курс недоступен — потребность в нём зарегистрирована внутри
       *  getRateFallbackMark, кеш реактивен, выражение пересчитается само.
       *  Одинаковые валюты сюда не попадают: заглушка для них null. */
      return null;
    }
    else if (sourcePrice.convertSync(sourceCurrencyCode, targetCurrencyCode, date)!.greaterThan(targetPrice) === true) {
      /** Курсы к этому месту уже в кеше — заглушка выше вернула null,
       *  поэтому обратное приведение тоже посчитается. */
      let priceInSourceCurrency = '';

      if (sourceCurrencyCode === Core.displayedCurrencyCode) {
        // валюта предложения и есть валюта платформы — цифра ниже уже в ней
      }
      else {
        priceInSourceCurrency = `, в валюте предложения — ${targetPrice.convertSync(targetCurrencyCode, sourceCurrencyCode, date)!.format(sourceCurrencyCode)}`;
      }

      /** Основная цифра — в валюте платформы, как остальные цены в реестре */
      return `Требуется цена не выше ${targetPrice.formatInDisplayCurrency(targetCurrencyCode, date)}${priceInSourceCurrency}`;
    }
    else {
      return null;
    }
  }
}

export default Mismatch;
